
import React, { useState, useEffect } from 'react';
import { Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { getLocalSettings, saveLocalSettings, LocalSettings } from '../services/localTranscriptionService';

const MODEL_OPTIONS = [
  { value: 'onnx-community/whisper-tiny', label: 'Tiny (~39M params, fastest)' },
  { value: 'onnx-community/whisper-base', label: 'Base (~74M params, balanced)' },
  { value: 'onnx-community/whisper-small', label: 'Small (~244M params, accurate)' },
];

const LANGUAGE_OPTIONS = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'nl', label: 'Dutch' },
  { value: 'pl', label: 'Polish' },
  { value: 'ru', label: 'Russian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ar', label: 'Arabic' },
  { value: 'hi', label: 'Hindi' },
];

const LocalModelSettings: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<LocalSettings>(getLocalSettings);

  useEffect(() => {
    saveLocalSettings(settings);
  }, [settings]);

  const set = <K extends keyof LocalSettings>(key: K, value: LocalSettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-xs text-base-content/60 dark:text-dark-content/60 hover:text-base-content dark:hover:text-dark-content transition-colors w-full"
        aria-expanded={open}
      >
        <Settings size={12} />
        <span>Local model settings</span>
        {open ? <ChevronUp size={12} className="ml-auto" /> : <ChevronDown size={12} className="ml-auto" />}
      </button>

      {open && (
        <div className="mt-2 p-3 rounded-xl bg-base-200 dark:bg-dark-200 border border-base-300 dark:border-dark-300 space-y-3 animate-fade-in">
          {/* Model size */}
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold text-base-content/70 dark:text-dark-content/70 uppercase tracking-wide">
              Model
            </span>
            <select
              value={settings.modelId}
              onChange={e => set('modelId', e.target.value)}
              className="w-full text-xs rounded-lg px-2 py-1.5 bg-white dark:bg-dark-100 border border-base-300 dark:border-dark-300 text-base-content dark:text-dark-content focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            >
              {MODEL_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>

          {/* Language */}
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold text-base-content/70 dark:text-dark-content/70 uppercase tracking-wide">
              Language
            </span>
            <select
              value={settings.language}
              onChange={e => set('language', e.target.value)}
              className="w-full text-xs rounded-lg px-2 py-1.5 bg-white dark:bg-dark-100 border border-base-300 dark:border-dark-300 text-base-content dark:text-dark-content focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
            >
              {LANGUAGE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>

          {/* Task */}
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold text-base-content/70 dark:text-dark-content/70 uppercase tracking-wide">
              Task
            </span>
            <div className="flex gap-2">
              {(['transcribe', 'translate'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => set('task', t)}
                  className={`flex-1 py-1 rounded-lg text-xs font-medium capitalize transition-colors ${
                    settings.task === t
                      ? 'bg-brand-primary text-white'
                      : 'bg-white dark:bg-dark-100 border border-base-300 dark:border-dark-300 text-base-content/70 dark:text-dark-content/70 hover:border-brand-primary/50'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            {settings.task === 'translate' && (
              <div className="space-y-1.5">
                <p className="text-[10px] text-base-content/50 dark:text-dark-content/50">
                  Translates audio to English regardless of source language.
                </p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.keepOriginal}
                    onChange={e => set('keepOriginal', e.target.checked)}
                    className="accent-brand-primary w-3.5 h-3.5"
                  />
                  <span className="text-[11px] text-base-content/70 dark:text-dark-content/70">
                    Also keep original language transcript
                  </span>
                </label>
              </div>
            )}
          </label>

          {/* Beam size */}
          <label className="block space-y-1">
            <span className="text-[11px] font-semibold text-base-content/70 dark:text-dark-content/70 uppercase tracking-wide">
              Beam size — {settings.beamSize}
            </span>
            <input
              type="range"
              min={1}
              max={5}
              step={1}
              value={settings.beamSize}
              onChange={e => set('beamSize', Number(e.target.value))}
              className="w-full accent-brand-primary"
            />
            <div className="flex justify-between text-[10px] text-base-content/40 dark:text-dark-content/40">
              <span>1 (faster)</span>
              <span>5 (accurate)</span>
            </div>
          </label>
        </div>
      )}
    </div>
  );
};

export default LocalModelSettings;
